import {
  type PropsWithChildren,
  ReactNode,
  useCallback,
  useContext,
  useMemo,
  useRef,
  useState,
} from 'react'
import {Pressable, StyleSheet, TextInput, View} from 'react-native'
import Animated from 'react-native-reanimated'
import {Image} from 'expo-image'
import {moderatePost, RichText as RichTextAPI} from '@atproto/api'
import {useFocusEffect, useNavigation} from '@react-navigation/native'
import {useRequest} from 'ahooks'
import {format} from 'date-fns'
import {isNil} from 'lodash'

import {
  type CommonNavigatorParams,
  type NativeStackScreenProps,
} from '#/lib/routes/types'
import {isMobileWeb} from '#/platform/detection'
import {emitProposalCreated} from '#/state/events'
import {useModerationOpts} from '#/state/preferences/moderation-opts'
import {
  fillThreadModerationCache,
  ThreadModerationCache,
} from '#/state/queries/post-thread'
import {useProfileQuery} from '#/state/queries/profile'
import {useSession} from '#/state/session'
import * as Toast from '#/view/com/util/Toast'
import {
  atoms as a,
  useBreakpoints,
  useLayoutBreakpoints,
  useTheme,
  web,
} from '#/alf'
import {Button} from '#/components/Button'
import * as Layout from '#/components/Layout'
import {ScrollbarOffsetContext} from '#/components/Layout/context'
import * as Prompt from '#/components/Prompt'
import ProposalStatusTag from '#/components/ProposalStatusTag'
import {RichText} from '#/components/RichText'
import {Text} from '#/components/Typography'
import server from '#/server'
import {attachmentUrl} from '#/server/rice/client'
import {
  type Proposal,
  type ProposalStatus,
  type VoteChoice,
} from '#/server/rice/types'
import ProposalAuthor from './ProposalAuthor'
import ProposalEmbeds from './ProposalEmbeds'
import parserHTMLFile, {type HTMLBlock} from './util'
import VoteConfirm, {type VoteConfirmRef} from './VoteConfirm'
const {Header} = Layout
import {useSetMinimalShellMode} from '#/state/shell'
import {CENTER_COLUMN_OFFSET, SCROLLBAR_OFFSET} from '#/components/Layout/const'

type Props = NativeStackScreenProps<CommonNavigatorParams, 'ProposalDetail'>

export default function ProposalDetailScreen({route}: Props) {
  const {proposalId} = route.params
  const headerRef = useRef<View | null>(null)
  const {currentAccount} = useSession()
  const navigation = useNavigation()
  const {gtMobile} = useBreakpoints()
  const delControl = Prompt.usePromptControl()
  const voteConfirmRef = useRef<VoteConfirmRef>(null)
  const {isWithinOffsetView} = useContext(ScrollbarOffsetContext)
  const {centerColumnOffset} = useLayoutBreakpoints()

  const {data: currentUserInfo} = useRequest(
    async () => {
      const res = await server.rice.getCurrentUser()
      return res
    },
    {
      ready: !!currentAccount?.did,
    },
  )

  /**
   * 提案正文是一个 HTML 附件 —— core 要先把 `attachId` 拆成 fileType/fileId
   * 再走 `/file/download` 拿 blob;rice 直接给了附件的 URL。
   */
  const {data: proposalInfo, run: reloadProposalInfo} = useRequest(
    async () => {
      const proposal = await server.rice.getProposal(proposalId)
      let blocks: HTMLBlock[] | undefined
      const contentUrl = attachmentUrl(proposal.attachment)
      if (contentUrl) {
        const res = await fetch(contentUrl)
        if (res.ok) {
          blocks = parserHTMLFile(await res.text())
        }
      }
      return {...proposal, blocks} as Proposal & {blocks?: HTMLBlock[]}
    },
    {
      ready: !!proposalId,
      refreshDeps: [proposalId],
    },
  )

  const {data: comments, run: reloadComments} = useRequest(
    async () => {
      const page = await server.rice.listComments(proposalId, {limit: 100})
      return page.data
    },
    {
      ready: !!proposalId,
      refreshDeps: [proposalId],
    },
  )

  const t = useTheme()

  // 我投了什么直接来自详情(rice 的 my_vote),不再单独请求一次
  const myVote = proposalInfo?.my_vote ?? null

  const reload = () => {
    reloadProposalInfo()
  }

  const [commentText, setCommentText] = useState('')
  const [submittingComment, setSubmittingComment] = useState(false)

  const onSubmitComment = useCallback(async () => {
    const content = commentText.trim()
    if (!content) return
    setSubmittingComment(true)
    try {
      await server.rice.createComment(proposalId, content)
      Toast.show('评论成功', 'check', 'center')
      setCommentText('')
      reloadComments()
    } catch (e: any) {
      Toast.show(e?.message || '评论失败', 'xmark', 'center')
    } finally {
      setSubmittingComment(false)
    }
  }, [commentText, proposalId, reloadComments])

  const onDeleteComment = useCallback(
    async (commentId: string) => {
      try {
        await server.rice.deleteComment(proposalId, commentId)
        Toast.show('删除成功', 'check', 'center')
        reloadComments()
      } catch (e: any) {
        Toast.show(e?.message || '删除失败', 'xmark', 'center')
      }
    },
    [proposalId, reloadComments],
  )

  const setMinimalShellMode = useSetMinimalShellMode()
  useFocusEffect(
    useCallback(() => {
      setMinimalShellMode(false)
    }, [setMinimalShellMode]),
  )

  // const mockEmbed = {
  //   $type: 'app.bsky.embed.images#view',
  //   images: [
  //     {
  //       thumb:
  //         'https://xiangjiandao.rivtower.cc/bsky/img/feed_thumbnail/plain/did:plc:pc5gxd5my6uooild5drcixdm/bafkreigzzzfwbraggh4zfxws6qladwrpwvpihscaqigkgb5zusvi6zfixu@jpeg',
  //       fullsize:
  //         'https://xiangjiandao.rivtower.cc/bsky/img/feed_fullsize/plain/did:plc:pc5gxd5my6uooild5drcixdm/bafkreigzzzfwbraggh4zfxws6qladwrpwvpihscaqigkgb5zusvi6zfixu@jpeg',
  //       alt: '',
  //       aspectRatio: {width: 474, height: 474},
  //     },
  //     {
  //       thumb:
  //         'https://xiangjiandao.rivtower.cc/bsky/img/feed_thumbnail/plain/did:plc:pc5gxd5my6uooild5drcixdm/bafkreigzzzfwbraggh4zfxws6qladwrpwvpihscaqigkgb5zusvi6zfixu@jpeg',
  //       fullsize:
  //         'https://xiangjiandao.rivtower.cc/bsky/img/feed_fullsize/plain/did:plc:pc5gxd5my6uooild5drcixdm/bafkreigzzzfwbraggh4zfxws6qladwrpwvpihscaqigkgb5zusvi6zfixu@jpeg',
  //       alt: '',
  //       aspectRatio: {width: 474, height: 474},
  //     },
  //   ],
  // }

  const author = proposalInfo?.author
  // 头像的权威在 AT Protocol 的 profile 记录,不在 rice —— 见
  // `lib/api/feed/proposal.ts` 的 hydrateAuthors。这里是详情页,只有一个人,
  // 直接走 profile 查询(缓存跟列表页共用,通常是命中的)。
  const {data: authorProfile} = useProfileQuery({did: author?.did})
  const mockAuthor = author
    ? {
        avatar: authorProfile?.avatar ?? attachmentUrl(author.avatar),
        did: author.did,
        displayName: author.nickname || author.handle,
        handle: author.handle,
        labels: authorProfile?.labels ?? [],
        viewer: authorProfile?.viewer ?? {muted: false, blockedBy: false},
      }
    : null

  const canDelProposal =
    currentAccount?.did && currentAccount?.did === author?.did
  const canVote =
    currentUserInfo?.node_member && !myVote && proposalInfo?.status === 'open'
  return (
    <Layout.Screen
      style={[
        styles.page,
        // isMobileWeb ? { height: '-webkit-fill-available' } : {},
        gtMobile && styles.maxPageWidth,

        // { paddingBottom: gtMobile ? 0 : 58 }
      ]}
      testID="proposalDetailScreen">
      {/* <PostThreadComponent uri={uri} /> */}
      <Header.Outer headerRef={headerRef}>
        <Header.BackButton defalutBackRoute="Hall" />
        <Header.Content>
          <Header.TitleText>提案</Header.TitleText>
        </Header.Content>
        <Header.Slot>
          {canDelProposal && (
            <Pressable
              accessibilityRole="button"
              onPress={() => {
                delControl.open()
              }}>
              <View style={[a.align_end]}>
                <Image
                  accessibilityIgnoresInvertColors
                  style={{width: 22, height: 22}}
                  source={require('#/assets/dustbin.svg')}
                />
              </View>
            </Pressable>
          )}
          {/* <ThreadMenu
            sortReplies={sortReplies}
            treeViewEnabled={treeViewEnabled}
            setSortReplies={updateSortReplies}
            setTreeViewEnabled={updateTreeViewEnabled}
            /> */}
        </Header.Slot>
      </Header.Outer>

      <View
        style={[
          a.px_lg,
          !isWithinOffsetView && {
            transform: [
              {translateX: centerColumnOffset ? CENTER_COLUMN_OFFSET : 0},
              {translateX: web(SCROLLBAR_OFFSET) ?? 0},
            ],
          },
          gtMobile ? {minHeight: 'calc(100% - 52px - 61px)'} : {},
        ]}>
        <View style={[a.pt_lg]}>
          {mockAuthor && <ProposalAuthor author={mockAuthor} />}
        </View>
        <View style={[a.pt_md]}>
          <Text style={[a.text_xl, a.font_bold]}>{proposalInfo?.title}</Text>
        </View>
        <View style={[a.flex_row, a.align_center, a.justify_between, a.my_md]}>
          <ProposalStatusTag status={proposalInfo?.status} />
          {proposalInfo?.inserted_at && (
            <View style={[a.flex_row, a.align_center, a.gap_xs]}>
              <Image
                accessibilityIgnoresInvertColors
                style={{width: 16, height: 16}}
                source={require('#/assets/clock.svg')}
              />
              <Text style={[a.text_xs, t.atoms.text_contrast_medium]}>
                {format(new Date(proposalInfo.inserted_at), 'yyyy/MM/dd HH:mm')}
              </Text>
            </View>
          )}
        </View>
        <View>
          <View style={[styles.spliter]} />
        </View>

        {/* <pre dangerouslySetInnerHTML={{ __html: proposalInfo?.content }} /> */}
        {proposalInfo?.blocks?.map((block, index) => {
          if (block.type === 'images') {
            return (
              <ProposalEmbeds
                key={index}
                embed={{
                  $type: 'app.bsky.embed.images#view',
                  images: block.srcs.map(imgSrc => ({
                    thumb: imgSrc,
                    fullsize: imgSrc,
                    alt: '',
                    aspectRatio: {width: 474, height: 474},
                  })),
                }}
              />
            )
          }
          return (
            <div
              style={{wordBreak: 'break-all'}}
              key={index}
              dangerouslySetInnerHTML={{__html: block.content}}
            />
          )
        })}
        {/* <View style={[styles.blockTitle]}>
            <Text style={[a.text_md]}>proposalId: {proposalId}</Text>
          </View>
          <View style={[styles.blockContent]}>
            <RichText
              enableTags
              selectable
              value={new RichTextAPI({ text: '<span style="color: red">3132</span>' })}
              style={[a.flex_1, a.text_xl]}
              // authorHandle={post.author.handle}
              shouldProxyLinks={true}
            />
            <View style={[a.py_xs]}>
              <ProposalEmbeds embed={mockEmbed} />
            </View>
          </View> */}
        <VoteResult
          agree={proposalInfo?.agree_count}
          disagree={proposalInfo?.oppose_count}
          status={proposalInfo?.status}
          endDate={proposalInfo?.closes_at}
          myVote={myVote}
        />

        {/* 评论区域 */}
        <View style={[a.mt_xl]}>
          <Text style={[a.text_md, a.font_bold, a.mb_md]}>
            评论 ({comments?.length || 0})
          </Text>
          {comments?.length === 0 ? (
            <Text style={[t.atoms.text_contrast_medium]}>
              暂无评论，快来发表第一条评论吧
            </Text>
          ) : (
            <View style={[a.gap_md]}>
              {comments?.map(comment => (
                <View
                  key={comment.id}
                  style={[
                    a.p_md,
                    {backgroundColor: '#F8F9FA', borderRadius: 8},
                  ]}>
                  <View
                    style={[
                      a.flex_row,
                      a.align_center,
                      a.justify_between,
                      a.mb_xs,
                    ]}>
                    <Text style={[a.text_sm, a.font_bold]}>
                      {comment.author?.nickname || comment.author?.handle}
                    </Text>
                    {comment.author?.id === currentUserInfo?.id ? (
                      <Pressable
                        accessibilityRole="button"
                        onPress={() => onDeleteComment(comment.id)}
                        hitSlop={8}>
                        <Image
                          accessibilityIgnoresInvertColors
                          style={{width: 16, height: 16}}
                          source={require('#/assets/dustbin.svg')}
                        />
                      </Pressable>
                    ) : null}
                  </View>
                  <Text
                    style={[
                      a.text_sm,
                      t.atoms.text_contrast_high,
                      {marginBottom: 6},
                    ]}>
                    {comment.body}
                  </Text>
                  <Text style={[a.text_xs, t.atoms.text_contrast_low]}>
                    {format(new Date(comment.inserted_at), 'yyyy/MM/dd HH:mm')}
                  </Text>
                </View>
              ))}
            </View>
          )}

          {currentAccount?.did ? (
            <View style={[a.mt_lg, a.gap_sm]}>
              <TextInput
                value={commentText}
                onChangeText={setCommentText}
                placeholder="发表你的评论..."
                multiline
                maxLength={512}
                style={[
                  a.p_md,
                  a.text_sm,
                  t.atoms.text_contrast_high,
                  {
                    borderWidth: 1,
                    borderColor: '#D4DBE2',
                    borderRadius: 8,
                    minHeight: 80,
                    textAlignVertical: 'top',
                  },
                ]}
              />
              <Pressable
                accessibilityRole="button"
                onPress={onSubmitComment}
                disabled={!commentText.trim() || submittingComment}
                style={[
                  a.align_self_end,
                  a.px_md,
                  a.py_sm,
                  {
                    backgroundColor:
                      !commentText.trim() || submittingComment
                        ? '#D4DBE2'
                        : '#1083FE',
                    borderRadius: 6,
                  },
                ]}>
                <Text style={[a.text_sm, {color: '#fff'}]}>
                  {submittingComment ? '发送中...' : '发送'}
                </Text>
              </Pressable>
            </View>
          ) : null}
        </View>

        {/* 投票框底部占位 */}
        <View style={{height: 140}} />
        {/* { canVote ? <View style={{ height: 40 }} /> : null} */}
      </View>

      {canVote ? (
        <View
          style={[
            a.flex_row,
            a.align_center,
            a.px_lg,
            a.gap_sm,
            a.py_sm,
            styles.voteActionBorder,
            // a.absolute,
            {
              position: gtMobile ? 'sticky' : 'fixed',
              bottom: gtMobile ? 0 : 58,
              left: 0,
              right: 0,
              backgroundColor: '#fff',
            },
            !isWithinOffsetView && {
              transform: [
                {translateX: centerColumnOffset ? CENTER_COLUMN_OFFSET : 0},
                {translateX: web(SCROLLBAR_OFFSET) ?? 0},
              ],
            },
          ]}>
          <SkewButton
            label="disagree"
            position="left"
            color="#FD615B"
            onPress={() => voteConfirmRef.current?.open('oppose')}>
            <Text style={[a.text_md, {color: '#fff'}]}>反对</Text>
          </SkewButton>
          <SkewButton
            label="agree"
            position="right"
            color="#1083FE"
            onPress={() => voteConfirmRef.current?.open('agree')}>
            <Text style={[a.text_md, {color: '#fff'}]}>同意</Text>
          </SkewButton>
        </View>
      ) : null}
      <Prompt.Basic
        control={delControl}
        title="要删除这则提案吗？"
        description="如果你删除这则提案，则以后将无法恢复。"
        confirmButtonCta="删除"
        onConfirm={async () => {
          await server.rice.deleteProposal(proposalId)
          Toast.show('删除成功', 'check', 'center')
          emitProposalCreated()
          navigation.goBack()
        }}
        confirmButtonColor="negative"
      />

      <VoteConfirm
        ref={voteConfirmRef}
        onConfirm={async voteFor => {
          await server.rice.vote(proposalId, voteFor)
          Toast.show('投票成功', 'check', 'center')
          reload()
          emitProposalCreated()
        }}
      />
    </Layout.Screen>
  )
}

type SkewButtonProps = {
  label: string
  position: 'left' | 'right'
  color: string
  onPress: () => void
}

function SkewButton({
  label,
  position,
  color,
  children,
  onPress,
}: PropsWithChildren<SkewButtonProps>) {
  return (
    <Button label={label} style={[styles.skewButton]} onPress={onPress}>
      <View style={[a.h_full, a.w_full]}>
        <View
          style={[
            styles.skewButtonBGRounded,
            {
              [position === 'left' ? 'left' : 'right']: 0,
              backgroundColor: color,
            },
          ]}
        />
        <View
          style={[
            styles.skewButtonBGSkew,
            {
              [position === 'left' ? 'right' : 'left']: 0,
              backgroundColor: color,
            },
          ]}
        />
        <View style={[a.h_full, a.align_center, a.justify_center]}>
          {children}
        </View>
      </View>
    </Button>
  )
}

type VoteResultProps = {
  agree: number | undefined
  disagree: number | undefined
  status: ProposalStatus | undefined
  endDate: string | undefined
  myVote: VoteChoice | null
}

function VoteResult(props: VoteResultProps) {
  const {
    agree: unsafe_agree,
    disagree: unsafe_disagree,
    status,
    endDate,
    myVote,
  } = props
  const t = useTheme()
  const {agreeVal, disagreeVal, agreePercent, disagreePercent} = useMemo(() => {
    const agree = unsafe_agree || 0
    const disagree = unsafe_disagree || 0
    const sum = agree + disagree
    if (sum === 0) {
      return {
        agreeVal: 0,
        disagreeVal: 0,
        agreePercent: '0%' as `${number}%`,
        disagreePercent: '0%' as `${number}%`,
      }
    }
    // const agreePercentVal = (agree / sum * 100).toFixed(0);
    return {
      agreeVal: agree,
      disagreeVal: disagree,
      agreePercent: `${((agree / sum) * 100).toFixed(0)}%` as `${number}%`,
      disagreePercent: `${((disagree / sum) * 100).toFixed(
        0,
      )}%` as `${number}%`,
    }
  }, [unsafe_agree, unsafe_disagree])
  return (
    <View style={[a.mb_2xl, a.mt_lg]}>
      <View style={[styles.spliter, a.mb_md]} />
      <View style={[a.flex_row, a.align_center, a.justify_between, a.py_xs]}>
        <View>
          <Text style={[a.text_sm, a.font_bold]}>
            总投票数: {agreeVal + disagreeVal}
          </Text>
        </View>
        {status && status !== 'open' ? (
          <Text style={[a.text_xs, a.font_bold, {color: '#FD615B'}]}>
            已结束
          </Text>
        ) : (
          <Text style={[a.text_xs, t.atoms.text_contrast_medium]}>
            截止时间:{' '}
            {endDate ? format(new Date(endDate), 'yyyy/MM/dd HH:mm:ss') : '-'}
          </Text>
        )}
      </View>
      <View style={[a.mt_sm, voteStyles.border, a.p_lg]}>
        {myVote && (
          <View style={[a.mb_md]}>
            <Text style={[a.text_xs, t.atoms.text_contrast_low]}>
              你已选: {myVote === 'agree' ? '同意' : '反对'}
            </Text>
          </View>
        )}

        <View style={[a.flex_row, a.align_center, a.gap_md, a.pb_md]}>
          <Text>同意</Text>
          <View style={voteStyles.bar}>
            <View
              style={[
                voteStyles.barInner,
                {backgroundColor: '#1083FE', width: agreePercent},
              ]}
            />
          </View>
          <View style={{width: 18}}>
            <Text>{agreeVal}</Text>
          </View>
          <View style={{width: 50}}>
            <Text>({agreePercent})</Text>
          </View>
        </View>
        <View style={[a.flex_row, a.align_center, a.gap_md]}>
          <Text>反对</Text>
          <View style={voteStyles.bar}>
            <View
              style={[
                voteStyles.barInner,
                {backgroundColor: '#FD615B', width: disagreePercent},
              ]}
            />
          </View>
          <View style={{width: 18}}>
            <Text>{disagreeVal}</Text>
          </View>
          <View style={{width: 50}}>
            <Text>({disagreePercent})</Text>
          </View>
        </View>
      </View>
    </View>
  )
}

const styles = StyleSheet.create({
  page: {
    minHeight: '100%',
    width: '100%',
  },
  maxPageWidth: {
    maxWidth: 600,
    alignSelf: 'center',
  },
  // main: {
  //   height: 'calc(100% - 52px - 58px)',
  // },
  blockTitle: {
    marginTop: 16,
    padding: 12,
    borderRadius: 6,
    backgroundColor: 'rgba(16, 131, 254, 0.1)',
  },
  blockContent: {
    marginTop: 16,
  },
  spliter: {
    height: 1,
    width: '100%',
    backgroundColor: '#D4DBE2',
  },
  voteButtonReject: {
    flex: 1,
    backgroundColor: '#FD615B',
    transform: 'skewX(-30deg) translateX(4px)',
    borderRadius: 10,
    width: '100%',
    // borderTopLeftRadius: 44,
    // borderBottomLeftRadius: 44,
    // borderTopRightRadius: 6,
    // borderBottomRightRadius: 100,
    height: 44,
  },
  voteButtonAgree: {
    flex: 1,
    backgroundColor: '#1083FE',
    transform: 'skewX(-30deg)  translateX(-4px)',
  },
  skewButton: {
    position: 'relative',
    height: 44,
    flex: 1,
  },
  skewButtonBGRounded: {
    position: 'absolute',
    top: 0,
    bottom: 0,
    width: '50%',
    borderRadius: 44,
    height: 44,
    flex: 1,
  },
  skewButtonBGSkew: {
    flex: 1,
    position: 'absolute',
    top: 0,
    bottom: 0,
    borderRadius: 10,
    width: '80%',
    height: 44,
    transform: 'skewX(-30deg)  translateX(-4px)',
  },
  voteActionBorder: {
    borderTopColor: '#D4DBE2',
    borderTopWidth: 0.5,
    borderStyle: 'solid',
  },
})

const voteStyles = StyleSheet.create({
  border: {
    borderWidth: 0.5,
    borderColor: '#d4dbe2',
    borderRadius: 12,
  },
  bar: {
    flex: 1,
    height: 10,
    borderRadius: 4,
    overflow: 'hidden',
    backgroundColor: '#F1F3F5',
  },
  barInner: {
    height: '100%',
  },
  value: {
    alignItems: 'center',
    justifyContent: 'center',
    flex: 0,
    flexBasis: 20,
    // width: 200,
  },
  percent: {
    alignItems: 'center',
    justifyContent: 'center',
    flex: 0,
    flexBasis: 40,
  },
})
